/**
 * RoadRenderPass.ts — renders OSM road polylines as crisp ribbon meshes on
 * top of the terrain. Replaces the old rasterised roadCoverage tint for the
 * actual road visual (the coverage texture is still kept around as a mask
 * for tree-scatter avoidance).
 *
 * Reads:  Camera, Lighting, baked RoadAsset, TerrainAsset (for Y sampling).
 * Writes: RenderTarget.color (load), RenderTarget.depth (load, reverse-Z).
 *
 * Geometry is built once at construction time. For each polyline:
 *   - segment perpendiculars are mitred at interior vertices,
 *   - vertex Y is sampled from the terrain heightfield plus a small lift to
 *     stay above z-fighting,
 *   - per-vertex attributes: position, uv=(alongDistance, lateral[-1..1]),
 *     params=(carriagewayWidthM, isMajor).
 */

import roadWgsl from "../shaders/road.wgsl?raw";
import type { Camera } from "../Camera";
import type { LightingUniforms } from "../rendering/LightingUniforms";
import type { ManagedRenderTarget } from "../rendering/RenderTarget";
import { REVERSE_Z_COMPARE } from "../rendering/RenderTarget";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import {
  createBufferFromTypedArray,
  createUniformBuffer,
} from "../gpu/Buffers";
import type { RoadAsset, RoadPolyline } from "../assets/RoadAsset";
import type { TerrainAsset } from "../assets/TerrainAsset";
import { sampleTerrainHeight } from "../assets/TerrainAsset";
import type { TerrainRenderPass } from "./TerrainRenderPass";

/** Lift in metres above terrain to keep the ribbon out of the heightfield. */
const ROAD_HEIGHT_OFFSET_M = 0.5;
/** Max segment length before subdivision so road Y tracks terrain bumps. */
const MAX_SEGMENT_LENGTH_M = 2.5;
/** Cap on mitre length / halfWidth to avoid spikes at sharp turns. */
const MAX_MITRE_RATIO = 1.4;

export interface RoadPassOptions {
  roads: RoadAsset;
  terrain: TerrainAsset;
  terrainPass: TerrainRenderPass;
}

export class RoadRenderPass implements RenderPass {
  readonly name = "RoadRenderPass";

  private pipeline: GPURenderPipeline;
  private bgl: GPUBindGroupLayout;
  private bindGroup: GPUBindGroup;
  private vertexBuffer: GPUBuffer | null = null;
  private indexBuffer: GPUBuffer | null = null;
  private indexCount = 0;
  private globalsBuffer: GPUBuffer;

  constructor(
    device: GPUDevice,
    rt: ManagedRenderTarget,
    camera: Camera,
    lighting: LightingUniforms,
    opts: RoadPassOptions,
  ) {
    const module = device.createShaderModule({
      label: "road.wgsl",
      code: roadWgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "RoadRenderPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "float" },
        },
      ],
    });
    this.pipeline = device.createRenderPipeline({
      label: "RoadRenderPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.bgl] }),
      vertex: {
        module,
        entryPoint: "vs_main",
        buffers: [
          {
            arrayStride: 8 * 4, // pos(3) + uv(2) + params(3)
            attributes: [
              { shaderLocation: 0, offset: 0, format: "float32x3" },
              { shaderLocation: 1, offset: 12, format: "float32x2" },
              { shaderLocation: 2, offset: 20, format: "float32x3" },
            ],
          },
        ],
      },
      fragment: {
        module,
        entryPoint: "fs_main",
        targets: [
          {
            format: rt.current.colorFormat,
            blend: {
              color: {
                srcFactor: "src-alpha",
                dstFactor: "one-minus-src-alpha",
                operation: "add",
              },
              alpha: {
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
                operation: "add",
              },
            },
          },
        ],
      },
      primitive: { topology: "triangle-list", cullMode: "none" },
      depthStencil: {
        format: rt.current.depthFormat,
        depthWriteEnabled: false,
        depthCompare: REVERSE_Z_COMPARE,
        // Reverse-Z: larger depth = closer. Positive bias pushes roads toward
        // the camera so terrain triangles between heightmap samples can't
        // poke through the ribbon edges.
        depthBias: 256,
        depthBiasSlopeScale: 6,
        depthBiasClamp: 0,
      },
    });
    this.globalsBuffer = createUniformBuffer(
      device,
      16,
      "RoadRenderPass.globals",
    );
    device.queue.writeBuffer(
      this.globalsBuffer,
      0,
      new Float32Array([opts.terrainPass.heightmap.extent, 0, 0, 0]),
    );
    this.bindGroup = device.createBindGroup({
      label: "RoadRenderPass.bg",
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: camera.buffer } },
        { binding: 1, resource: { buffer: lighting.buffer } },
        { binding: 2, resource: { buffer: this.globalsBuffer } },
        { binding: 3, resource: opts.terrainPass.polygonFadeView() },
      ],
    });
    this.uploadGeometry(device, opts.roads, opts.terrain);
  }

  private uploadGeometry(
    device: GPUDevice,
    roads: RoadAsset,
    terrain: TerrainAsset,
  ): void {
    const verts: number[] = [];
    const indices: number[] = [];
    let vbase = 0;
    // Render narrowest roads first so wider classes overpaint them cleanly at
    // intersections (alpha blend, depthWriteEnabled = false).
    const sorted = [...roads.roads].sort((a, b) => a.width_m - b.width_m);
    // OSM frequently splits a single road into two ways at a river crossing
    // (one per shore) without a connecting `bridge=yes` way. Detect endpoint
    // pairs that face each other across water and inject a synthetic bridge
    // polyline so the road visually crosses.
    const synthetic = buildWaterCrossings(sorted, terrain);
    const allRoads = [...sorted, ...synthetic].sort(
      (a, b) => a.width_m - b.width_m,
    );
    for (const road of allRoads) {
      const rawPts = road.points;
      if (rawPts.length < 2) continue;
      // Subdivide so each segment is at most MAX_SEGMENT_LENGTH_M; this lets
      // the per-vertex terrain sample follow heightmap bumps closely enough
      // that the ribbon doesn't get pierced from below on hilly stretches.
      const pts: Array<[number, number]> = [rawPts[0]!];
      for (let i = 1; i < rawPts.length; i++) {
        const a = rawPts[i - 1]!;
        const b = rawPts[i]!;
        const dx = b[0] - a[0];
        const dz = b[1] - a[1];
        const len = Math.hypot(dx, dz);
        const steps = Math.max(1, Math.ceil(len / MAX_SEGMENT_LENGTH_M));
        for (let s = 1; s <= steps; s++) {
          const t = s / steps;
          pts.push([a[0] + dx * t, a[1] + dz * t]);
        }
      }
      const n = pts.length;
      if (n < 2) continue;
      const halfW = road.width_m * 0.5;
      const isMajor = road.category === "major_road" ? 1 : 0;

      // Per-segment unit perpendiculars and lengths.
      const segPerp: Array<[number, number]> = new Array(n - 1);
      const segLen: number[] = new Array(n - 1);
      for (let i = 0; i < n - 1; i++) {
        const ax = pts[i]![0];
        const az = pts[i]![1];
        const bx = pts[i + 1]![0];
        const bz = pts[i + 1]![1];
        const dx = bx - ax;
        const dz = bz - az;
        const len = Math.hypot(dx, dz);
        segLen[i] = len;
        if (len < 1e-4) {
          segPerp[i] = [0, 0];
        } else {
          // 90° CCW in XZ plane (x,z) → (-z, x).
          segPerp[i] = [-dz / len, dx / len];
        }
      }

      // Per-vertex mitre direction (averaged perpendicular, scaled by 1/cos(theta/2)).
      // Two clamps: a global ratio cap, and a per-vertex length cap derived
      // from the shorter adjacent segment so the offset never reaches the
      // next polyline vertex (otherwise sharp bends spawn flag-shaped spikes).
      const mitre: Array<[number, number]> = new Array(n);
      for (let i = 0; i < n; i++) {
        const pPrev = i > 0 ? segPerp[i - 1]! : null;
        const pNext = i < n - 1 ? segPerp[i]! : null;
        let dir: [number, number] = [0, 0];
        if (pPrev && pNext) {
          const mx = pPrev[0] + pNext[0];
          const mz = pPrev[1] + pNext[1];
          const ml = Math.hypot(mx, mz);
          if (ml < 1e-3) {
            dir = pNext;
          } else {
            const scale = Math.min(MAX_MITRE_RATIO, 2 / ml);
            dir = [(mx / ml) * scale, (mz / ml) * scale];
          }
        } else if (pNext) {
          dir = pNext;
        } else if (pPrev) {
          dir = pPrev;
        }
        const offsetLen = Math.hypot(dir[0], dir[1]) * halfW;
        const adjMin = Math.min(
          i > 0 ? segLen[i - 1]! : Infinity,
          i < n - 1 ? segLen[i]! : Infinity,
        );
        // Allow the full base half-width, then cap only the *extra* mitre
        // extension so sharp bends don't spike but straight (or subdivided)
        // runs keep their nominal width.
        const cap = halfW + adjMin * 0.45;
        if (offsetLen > cap && offsetLen > 1e-4) {
          const k = cap / offsetLen;
          dir = [dir[0] * k, dir[1] * k];
        }
        mitre[i] = dir;
      }

      // Cumulative along-distance at each vertex (metres).
      const alongAt: number[] = new Array(n);
      alongAt[0] = 0;
      for (let i = 1; i < n; i++) {
        alongAt[i] = alongAt[i - 1]! + segLen[i - 1]!;
      }

      // Emit 2 vertices per polyline point (left, right).
      const totalLen = alongAt[n - 1]!;

      // Anywhere the heightmap drops below mean water level the road must
      // become a flat bridge deck spanning from one shore to the other
      // instead of dipping into the riverbed. Deck height matches
      // WorldObjectBridgesBuilder.DEFAULT_DECK_HEIGHT_M so the road sits on
      // top of authored bridge models.
      const BRIDGE_DECK_ABOVE_WATER_M = 3.5;
      const centerY: number[] = new Array(n);
      const overWater: boolean[] = new Array(n);
      for (let i = 0; i < n; i++) {
        const h = sampleTerrainHeight(terrain, pts[i]![0], pts[i]![1]);
        overWater[i] = h < 0;
        centerY[i] = Math.max(h, 0);
      }
      let i = 0;
      while (i < n) {
        if (!overWater[i]) {
          i++;
          continue;
        }
        let j = i;
        while (j < n && overWater[j]) j++;
        // Deck height = max land height within SHORE_LOOKBACK_M back along
        // the polyline from each shore vertex. The exact shoreline sample is
        // usually right at water level (≈0) so just using centerY[i-1] makes
        // the deck dip well below the inland road; we want the deck flush
        // with the surrounding ground.
        const SHORE_LOOKBACK_M = 25;
        const shoreHigh = (
          fromIdx: number,
          step: number,
          startAlong: number,
        ): number => {
          let best = 0;
          let k = fromIdx;
          while (k >= 0 && k < n) {
            const along = alongAt[k]!;
            if (Math.abs(along - startAlong) > SHORE_LOOKBACK_M) break;
            if (!overWater[k] && centerY[k]! > best) best = centerY[k]!;
            k += step;
          }
          return best;
        };
        const leftY = i > 0 ? shoreHigh(i - 1, -1, alongAt[i - 1]!) : 0;
        const rightY = j < n ? shoreHigh(j, +1, alongAt[j]!) : 0;
        const minDeck = BRIDGE_DECK_ABOVE_WATER_M;
        // Per-shore deck height: each side at least its own shore land
        // height (clamped to minDeck). The bridge tilts linearly from
        // leftDeck to rightDeck so an asymmetric crossing doesn't create a
        // vertical step on the lower bank.
        const leftDeck = Math.max(leftY, minDeck);
        const rightDeck = Math.max(rightY, minDeck);
        const uLeft = i > 0 ? alongAt[i - 1]! : alongAt[i]!;
        const uRight = j < n ? alongAt[j]! : alongAt[j - 1]!;
        const span = Math.max(uRight - uLeft, 1e-3);
        for (let k = i; k < j; k++) {
          const t = (alongAt[k]! - uLeft) / span;
          centerY[k] = leftDeck * (1 - t) + rightDeck * t;
        }
        i = j;
      }
      // Approach ramps: within APPROACH_RAMP_M of any over-water vertex,
      // lift the road surface to the deck height. We hard-clamp (not lerp)
      // because the deck Y was already chosen as max of land within the
      // same lookback window, so the approach segment ground is at most
      // deck Y; clamping flush avoids the V-dip seen with partial lerps.
      const APPROACH_RAMP_M = 25;
      const blended = centerY.slice();
      for (let k = 0; k < n; k++) {
        if (overWater[k]) continue;
        let dist = Infinity;
        let deckRef = centerY[k]!;
        for (let m = 0; m < n; m++) {
          if (!overWater[m]) continue;
          const d = Math.abs(alongAt[k]! - alongAt[m]!);
          if (d < dist) {
            dist = d;
            deckRef = centerY[m]!;
          }
        }
        if (dist < APPROACH_RAMP_M) {
          blended[k] = Math.max(centerY[k]!, deckRef);
        }
      }
      for (let k = 0; k < n; k++) centerY[k] = blended[k]!;

      for (let i = 0; i < n; i++) {
        const [cx, cz] = pts[i]!;
        const [mx, mz] = mitre[i]!;
        const lx = cx + mx * halfW;
        const lz = cz + mz * halfW;
        const rx = cx - mx * halfW;
        const rz = cz - mz * halfW;
        // Use the smoothed centerline Y for BOTH left and right vertices so
        // the deck stays flat across the road width (no cross-slope tilt
        // from sampling the riverbank a half-width away).
        const y = centerY[i]! + ROAD_HEIGHT_OFFSET_M;
        const u = alongAt[i]!;
        const endFade = Math.min(u, totalLen - u);
        // Left vertex (v = -1).
        verts.push(lx, y, lz, u, -1, road.width_m, isMajor, endFade);
        // Right vertex (v = +1).
        verts.push(rx, y, rz, u, 1, road.width_m, isMajor, endFade);
      }

      // Indices: for each segment two triangles (L0, R0, R1, L0, R1, L1).
      for (let i = 0; i < n - 1; i++) {
        const l0 = vbase + i * 2;
        const r0 = l0 + 1;
        const l1 = l0 + 2;
        const r1 = l0 + 3;
        indices.push(l0, r0, r1, l0, r1, l1);
      }
      vbase += n * 2;
    }

    if (indices.length === 0) {
      return;
    }
    this.vertexBuffer = createBufferFromTypedArray(
      device,
      new Float32Array(verts),
      GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
      "RoadRenderPass.vb",
    );
    this.indexBuffer = createBufferFromTypedArray(
      device,
      new Uint32Array(indices),
      GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
      "RoadRenderPass.ib",
    );
    this.indexCount = indices.length;
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    if (!this.vertexBuffer || !this.indexBuffer || this.indexCount === 0) {
      return;
    }
    const pass = encoder.beginRenderPass({
      label: "RoadRenderPass",
      colorAttachments: [
        {
          view: frame.renderTarget.colorView,
          loadOp: "load",
          storeOp: "store",
        },
      ],
      depthStencilAttachment: {
        view: frame.renderTarget.depthView,
        depthLoadOp: "load",
        depthStoreOp: "store",
      },
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.setVertexBuffer(0, this.vertexBuffer);
    pass.setIndexBuffer(this.indexBuffer, "uint32");
    pass.drawIndexed(this.indexCount, 1, 0, 0, 0);
    pass.end();
  }

  destroy(): void {
    this.vertexBuffer?.destroy();
    this.indexBuffer?.destroy();
    this.globalsBuffer.destroy();
  }
}

/**
 * OSM often splits roads at riverbanks without an explicit `bridge=yes` way,
 * leaving a visual gap over the water. For each ordered pair of polyline
 * endpoints belonging to different polylines, check if:
 *   - they're within a reasonable bridge distance,
 *   - both lie on/near the shoreline (sample ≈ 0 or just below),
 *   - the segment between them is mostly over water,
 *   - the connecting direction roughly continues each polyline's tangent.
 * If so, emit a synthetic bridge polyline of the wider class.
 */
function buildWaterCrossings(
  roads: RoadPolyline[],
  terrain: TerrainAsset,
): RoadPolyline[] {
  const MAX_BRIDGE_M = 220;
  const MIN_BRIDGE_M = 4;
  const WATER_SAMPLES = 20;
  const WATER_FRACTION_MIN = 0.6;
  const SHORE_PROBE_M = 60;
  // Both endpoints' outgoing tangents must point roughly toward each other
  // (dot with the connecting direction > MIN_DOT). Parallel roads running
  // along the same canal bank have tangents nearly perpendicular to the
  // cross-water connector, so they get rejected here.
  const MIN_DOT = 0.5;
  const out: RoadPolyline[] = [];
  type Endpoint = {
    road: RoadPolyline;
    point: [number, number];
    /** Outgoing tangent pointing away from the road body. */
    tangent: [number, number];
    roadIndex: number;
    isStart: boolean;
  };
  const endpoints: Endpoint[] = [];
  for (let r = 0; r < roads.length; r++) {
    const road = roads[r]!;
    const pts = road.points;
    if (pts.length < 2) continue;
    const a = pts[0]!;
    const b = pts[1]!;
    const c = pts[pts.length - 2]!;
    const d = pts[pts.length - 1]!;
    const tStart = normalize([a[0] - b[0], a[1] - b[1]]);
    const tEnd = normalize([d[0] - c[0], d[1] - c[1]]);
    // Shoreline filter: any water within SHORE_PROBE_M of the endpoint in
    // ANY direction qualifies (the road may run parallel to the bank).
    if (waterNearby(terrain, a, SHORE_PROBE_M)) {
      endpoints.push({
        road,
        point: a,
        tangent: tStart,
        roadIndex: r,
        isStart: true,
      });
    }
    if (waterNearby(terrain, d, SHORE_PROBE_M)) {
      endpoints.push({
        road,
        point: d,
        tangent: tEnd,
        roadIndex: r,
        isStart: false,
      });
    }
  }
  // For every shoreline endpoint, find its best counterpart (lowest cost).
  // Accept only mutual best pairs to avoid spurious connections.
  type Match = { idx: number; cost: number };
  const bestFor: Array<Match | null> = endpoints.map(() => null);
  for (let i = 0; i < endpoints.length; i++) {
    const a = endpoints[i]!;
    for (let j = 0; j < endpoints.length; j++) {
      if (j === i) continue;
      const b = endpoints[j]!;
      if (b.roadIndex === a.roadIndex) continue;
      const dx = b.point[0] - a.point[0];
      const dz = b.point[1] - a.point[1];
      const dist = Math.hypot(dx, dz);
      if (dist < MIN_BRIDGE_M || dist > MAX_BRIDGE_M) continue;
      const dirAB: [number, number] = [dx / dist, dz / dist];
      const dotA = a.tangent[0] * dirAB[0] + a.tangent[1] * dirAB[1];
      const dotB = -(b.tangent[0] * dirAB[0] + b.tangent[1] * dirAB[1]);
      if (dotA < MIN_DOT || dotB < MIN_DOT) continue;
      let waterHits = 0;
      const hA = sampleTerrainHeight(terrain, a.point[0], a.point[1]);
      const hB = sampleTerrainHeight(terrain, b.point[0], b.point[1]);
      const shoreY = Math.min(hA, hB);
      for (let s = 1; s < WATER_SAMPLES; s++) {
        const t = s / WATER_SAMPLES;
        const x = a.point[0] + dx * t;
        const z = a.point[1] + dz * t;
        const h = sampleTerrainHeight(terrain, x, z);
        if (h < 0.0 || h < shoreY - 0.5) waterHits++;
      }
      if (waterHits < WATER_SAMPLES * WATER_FRACTION_MIN) continue;
      // Cost: prefer short spans.
      const cost = dist;
      const cur = bestFor[i];
      if (!cur || cost < cur.cost) bestFor[i] = { idx: j, cost };
    }
  }
  // Sort all candidate pairs by cost; greedy-assign so each endpoint is used
  // at most once, but we don't require mutual best (oblique approaches mean
  // a's best partner is rarely a's reciprocal).
  type Pair = { i: number; j: number; cost: number };
  const pairs: Pair[] = [];
  for (let i = 0; i < endpoints.length; i++) {
    const m = bestFor[i];
    if (!m) continue;
    pairs.push({ i, j: m.idx, cost: m.cost });
  }
  pairs.sort((p, q) => p.cost - q.cost);
  const used = new Set<number>();
  for (const p of pairs) {
    if (used.has(p.i) || used.has(p.j)) continue;
    used.add(p.i);
    used.add(p.j);
    const a = endpoints[p.i]!;
    const b = endpoints[p.j]!;
    const width = Math.max(a.road.width_m, b.road.width_m);
    const cat: typeof a.road.category =
      a.road.category === "major_road" || b.road.category === "major_road"
        ? "major_road"
        : "road";
    out.push({
      category: cat,
      class: a.road.class,
      width_m: width,
      lanes: a.road.lanes ?? b.road.lanes ?? null,
      points: [a.point, b.point],
    });
  }
  // eslint-disable-next-line no-console
  console.info(
    `[RoadRenderPass] water-crossing bridges synthesised: ${out.length} ` +
      `(from ${endpoints.length} shoreline endpoints, ${roads.length} polylines)`,
  );
  return out;
}

function waterNearby(
  terrain: TerrainAsset,
  origin: [number, number],
  rangeM: number,
): boolean {
  const hOrigin = sampleTerrainHeight(terrain, origin[0], origin[1]);
  const RINGS = 4;
  const DIRS = 16;
  for (let ri = 1; ri <= RINGS; ri++) {
    const r = (ri / RINGS) * rangeM;
    for (let di = 0; di < DIRS; di++) {
      const a = (di / DIRS) * Math.PI * 2;
      const x = origin[0] + Math.cos(a) * r;
      const z = origin[1] + Math.sin(a) * r;
      const h = sampleTerrainHeight(terrain, x, z);
      if (h < 0.0 || h < hOrigin - 0.5) return true;
    }
  }
  return false;
}

function normalize(v: [number, number]): [number, number] {
  const l = Math.hypot(v[0], v[1]);
  if (l < 1e-6) return [0, 0];
  return [v[0] / l, v[1] / l];
}
